(() => {
  const controllers = new Map();
  const initialize = scope => {
    scope.querySelectorAll('.mb-landing').forEach(root => {
      if (controllers.has(root)) return;
      const lifecycle = new AbortController();
      const on = (node, type, handler, options = {}) => node?.addEventListener(type, handler, { ...options, signal: lifecycle.signal });
      const cleanup = [];
      controllers.set(root, () => { lifecycle.abort(); cleanup.forEach(fn => fn()); });
      const sticky = root.querySelector('.mb-sticky');
      const heroOrder = root.querySelector('#murder-box-order');
      const finalOrder = root.querySelector('#murder-box-final-order');
      if (sticky && heroOrder && finalOrder && 'IntersectionObserver' in window) {
        const updateSticky = () => {
          const hero = heroOrder.getBoundingClientRect();
          const final = finalOrder.querySelector('.mb-buy').getBoundingClientRect();
          sticky.classList.toggle('is-visible', hero.bottom < 0 && !(final.bottom > 0 && final.top < window.innerHeight));
        };
        const observer = new IntersectionObserver(updateSticky);
        observer.observe(heroOrder);
        observer.observe(finalOrder.querySelector('.mb-buy'));
        on(window, 'resize', updateSticky);
        updateSticky();
        cleanup.push(() => observer.disconnect());
      }
      root.querySelectorAll('[data-mb-gallery]').forEach(gallery => {
        const track = gallery.querySelector('.mb-media__track');
        const slides = [...gallery.querySelectorAll('.mb-media__slide')];
        if (!slides.length) return;
        const segments = [...gallery.querySelectorAll('.mb-media__segments span')];
        const thumbs = [...gallery.querySelectorAll('[data-mb-thumb]')];
        const pause = gallery.querySelector('[data-mb-pause]');
        const counter = gallery.querySelector('[data-mb-counter]');
        const caption = gallery.querySelector('[data-mb-caption]');
        const reduced = matchMedia('(prefers-reduced-motion: reduce)');
        let index = 0, timer, scrollTimer, userPaused = reduced.matches || slides.length < 2, hovered = false, focused = false, visible = true;
        gallery.querySelector('.mb-media__controls').hidden = false;
        gallery.querySelector('.mb-media__thumbs').hidden = false;
        const stop = () => { clearTimeout(timer); segments.forEach(el => el.classList.remove('is-running')); };
        const schedule = () => {
          stop();
          const pauseLabel = userPaused ? gallery.dataset.playLabel : gallery.dataset.pauseLabel;
          pause.querySelector('.mb-media__pause-text').textContent = pauseLabel;
          pause.querySelector('[data-mb-pause-icon]').textContent = userPaused ? '▶' : 'Ⅱ';
          pause.setAttribute('aria-label', pauseLabel);
          pause.setAttribute('aria-pressed', String(userPaused));
          if (userPaused || hovered || focused || !visible || document.hidden || reduced.matches || !['image', 'editorial'].includes(slides[index].dataset.mediaType)) return;
          segments[index].classList.add('is-running');
          timer = setTimeout(() => go(index + 1, false), 5000);
        };
        const update = next => {
          index = Math.max(0, Math.min(slides.length - 1, next));
          slides.forEach((slide, i) => {
            slide.inert = i !== index;
            slide.setAttribute('aria-hidden', String(i !== index));
            if (i !== index) slide.querySelectorAll('video').forEach(video => video.pause());
          });
          segments.forEach((el, i) => el.classList.toggle('is-active', i === index));
          thumbs.forEach((el, i) => el.setAttribute('aria-current', String(i === index)));
          counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
          caption.textContent = slides[index].dataset.caption;
          schedule();
        };
        const go = (next, manual = true) => {
          if (manual) userPaused = true;
          next = (next + slides.length) % slides.length;
          track.scrollTo({left: next * track.clientWidth, behavior: reduced.matches ? 'instant' : 'smooth'});
          update(next);
        };
        on(gallery.querySelector('[data-mb-prev]'), 'click', () => go(index - 1));
        on(gallery.querySelector('[data-mb-next]'), 'click', () => go(index + 1));
        thumbs.forEach((thumb, i) => on(thumb, 'click', () => go(i)));
        on(track, 'keydown', event => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); go(index + (event.key === 'ArrowRight' ? 1 : -1)); }
        });
        on(track, 'pointerdown', () => { userPaused = true; schedule(); });
        on(track, 'wheel', event => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) { userPaused = true; schedule(); } }, {passive: true});
        on(track, 'scroll', () => { clearTimeout(scrollTimer); scrollTimer = setTimeout(() => update(Math.round(track.scrollLeft / track.clientWidth)), 120); }, {passive:true});
        on(gallery, 'mouseenter', () => { hovered = true; schedule(); });
        on(gallery, 'mouseleave', () => { hovered = false; schedule(); });
        on(gallery, 'focusin', () => { focused = true; schedule(); });
        on(gallery, 'focusout', event => { if (!gallery.contains(event.relatedTarget)) { focused = false; schedule(); } });
        on(pause, 'click', () => { userPaused = !userPaused; schedule(); });
        on(document, 'visibilitychange', schedule);
        on(reduced, 'change', () => { if (reduced.matches) userPaused = true; schedule(); });
        on(window, 'resize', () => { track.scrollTo({left:index * track.clientWidth, behavior:'instant'}); });
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); }, {threshold:.2});
          observer.observe(gallery);
          cleanup.push(() => observer.disconnect());
        }
        const dialog = gallery.querySelector('.mb-lightbox');
        const viewer = dialog.querySelector('.mb-lightbox__images');
        const photos = [...viewer.querySelectorAll('figure')];
        let photoIndex = 0, opener, pointerStart;
        const setPhoto = next => {
          photoIndex = (next + photos.length) % photos.length;
          photos.forEach((figure, i) => {
            figure.classList.remove('is-zoomed');
            figure.inert = i !== photoIndex;
            figure.setAttribute('aria-hidden', String(i !== photoIndex));
            const image = figure.querySelector('img');
            if (Math.abs(i - photoIndex) <= 1 || i === 0 || i === photos.length - 1) image.src = image.dataset.src;
          });
          viewer.scrollTo({left:photoIndex * viewer.clientWidth, behavior:'instant'});
          dialog.querySelector('[data-mb-lightbox-caption]').textContent = photos[photoIndex].querySelector('img').alt;
        };
        slides.forEach((slide, i) => on(slide.querySelector('[data-mb-zoom]'), 'click', event => {
          if (!dialog.showModal) return;
          userPaused = true; schedule(); opener = event.currentTarget;
          dialog.showModal();
          setPhoto(photos.findIndex(photo => Number(photo.dataset.slideIndex) === i));
          gallery.classList.add('has-viewer');
        }));
        on(dialog.querySelector('[data-mb-close]'), 'click', () => dialog.close());
        on(dialog, 'close', () => { photos.forEach(photo => photo.classList.remove('is-zoomed')); gallery.classList.remove('has-viewer'); opener?.focus(); });
        on(dialog.querySelector('[data-mb-lightbox-prev]'), 'click', () => setPhoto(photoIndex - 1));
        on(dialog.querySelector('[data-mb-lightbox-next]'), 'click', () => setPhoto(photoIndex + 1));
        on(dialog, 'keydown', event => { if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); setPhoto(photoIndex + (event.key === 'ArrowRight' ? 1 : -1)); } });
        on(viewer, 'scroll', () => {
          const next = Math.round(viewer.scrollLeft / viewer.clientWidth);
          if (next !== photoIndex && !viewer.querySelector('.is-zoomed')) {
            photoIndex = next;
            photos.forEach((figure, i) => { figure.inert = i !== next; figure.setAttribute('aria-hidden', String(i !== next)); });
            photos.slice(Math.max(0,next-1), next+2).forEach(photo => {const image = photo.querySelector('img'); image.src = image.dataset.src;});
            dialog.querySelector('[data-mb-lightbox-caption]').textContent = photos[next]?.querySelector('img').alt || '';
          }
        }, {passive:true});
        photos.forEach(photo => on(photo, 'dblclick', () => photo.classList.toggle('is-zoomed')));
        on(viewer, 'pointerdown', event => { pointerStart = {x:event.clientX,y:event.clientY}; });
        on(viewer, 'pointerup', event => {
          if (pointerStart && event.clientY-pointerStart.y > 100 && Math.abs(event.clientX-pointerStart.x) < 50 && !viewer.querySelector('.is-zoomed') && (!window.visualViewport || window.visualViewport.scale <= 1)) dialog.close();
          pointerStart = null;
        });
        update(0);
        cleanup.push(() => { stop(); clearTimeout(scrollTimer); if (dialog.open) dialog.close(); });
      });
      root.querySelectorAll('[data-mb-strip]').forEach(strip => {
        const track = strip.querySelector('[data-mb-strip-track]');
        const counter = strip.querySelector('[data-mb-strip-counter]');
        const cards = [...track.children];
        const updateStripCounter = () => {
          if (!cards.length) return;
          const offset = track.getBoundingClientRect().left;
          const nearest = cards.reduce((best,card,i)=>Math.abs(card.getBoundingClientRect().left-offset)<Math.abs(cards[best].getBoundingClientRect().left-offset)?i:best,0);
          counter.textContent = `${String(nearest+1).padStart(2,'0')} / ${String(cards.length).padStart(2,'0')}`;
        };
        on(track,'scroll',updateStripCounter,{passive:true});
        updateStripCounter();
      });
      const previousStickyHeight = document.body.style.getPropertyValue('--mb-sticky-height');
      const resizeSticky = () => {
        const height = `${sticky?.offsetHeight || 0}px`;
        root.style.setProperty('--mb-sticky-height', height);
        document.body.style.setProperty('--mb-sticky-height', height);
      };
      cleanup.push(() => {
        if (previousStickyHeight) document.body.style.setProperty('--mb-sticky-height', previousStickyHeight);
        else document.body.style.removeProperty('--mb-sticky-height');
      });
      resizeSticky();
      if ('ResizeObserver' in window && sticky) {const observer = new ResizeObserver(resizeSticky);observer.observe(sticky);cleanup.push(()=>observer.disconnect());}
    });
  };
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((dispose, root) => { if (event.target.contains(root)) { dispose(); controllers.delete(root); } });
  });
})();
